import { useCallback, useEffect, useState } from 'react';
import { CreditCard, ExternalLink, Link2, Plus, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import PageShell from '../components/PageShell';
import StatCard from '../components/StatCard';
import DataTable, { type Column } from '../components/DataTable';
import Tabs from '../components/Tabs';
import Modal from '../components/Modal';
import StatusPill from '../components/StatusPill';
import FilterBar, {
  controlClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../components/FilterBar';
import { formatCurrency, formatDateTime } from '../lib/format';
import {
  createStripePaymentLink,
  fetchStripeCustomers,
  fetchStripePayments,
  fetchStripeStatus,
  fetchStripeWebhookEvents,
  fetchWebhooks,
  type StripeStatusResponse,
  type WebhookEvent,
} from '../lib/api';

type Row = Record<string, unknown>;
type TabId = 'payments' | 'customers' | 'webhooks';

const PAY_CHECKOUT_URL = 'https://pay.zerotail.org';
const WEBHOOK_URL = 'https://accounting.zerotail.org/api/v1/webhooks/stripe';

function str(row: Row, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '' && typeof value !== 'object') {
      return String(value);
    }
  }
  return null;
}

function Stripe() {
  const [tab, setTab] = useState<TabId>('payments');
  const [rows, setRows] = useState<Row[]>([]);
  const [webhookRows, setWebhookRows] = useState<WebhookEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<StripeStatusResponse | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);

  const loadStatus = useCallback(async () => {
    const result = await fetchStripeStatus();
    if (result.ok && result.data) setStatus(result.data);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    if (tab === 'payments') {
      const result = await fetchStripePayments(50);
      if (result.ok && result.data) setRows(result.data.data ?? []);
      else setRows([]);
    } else if (tab === 'customers') {
      const result = await fetchStripeCustomers(50);
      if (result.ok && result.data) setRows(result.data.data ?? []);
      else setRows([]);
    } else {
      const [cached, received] = await Promise.all([
        fetchStripeWebhookEvents(20),
        fetchWebhooks({ page: 1, page_size: 20, source: 'stripe' }),
      ]);
      if (received.ok && received.data?.items?.length) {
        setWebhookRows(received.data.items);
      } else if (cached.ok && cached.data?.data?.length) {
        setWebhookRows(
          (cached.data.data as Row[]).map((item, index) => ({
            id: index,
            source: 'stripe',
            status: str(item, 'status') ?? 'processed',
            signature_status: 'verified',
            event_id: str(item, 'id'),
            received_at: str(item, 'received_at') ?? new Date().toISOString(),
            retry_count: 0,
            idempotency_key: str(item, 'id') ?? `stripe-cache-${index}`,
            headers: {},
            metadata: {},
            created_at: str(item, 'received_at') ?? new Date().toISOString(),
          }))
        );
      } else {
        setWebhookRows([]);
      }
      setRows([]);
    }
    setLoading(false);
  }, [tab]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    void load();
  }, [load]);

  const paymentColumns: Column<Row>[] = [
    {
      key: 'created',
      header: 'Date',
      render: (r) => formatDateTime(str(r, 'created') ? Number(str(r, 'created')) * 1000 : null),
    },
    { key: 'id', header: 'Payment ID', render: (r) => str(r, 'id') ?? '—' },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (r) => {
        const amount = Number(str(r, 'amount') ?? 0) / 100;
        return formatCurrency(amount, (str(r, 'currency') ?? 'usd').toUpperCase());
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <StatusPill status={str(r, 'status') ?? 'unknown'} />,
    },
    {
      key: 'invoice_ref',
      header: 'Invoice ref',
      render: (r) => {
        const metadata = r.metadata as Record<string, unknown> | undefined;
        return str(r, 'invoice_ref') ?? (metadata?.invoice_ref ? String(metadata.invoice_ref) : '—');
      },
    },
  ];

  const customerColumns: Column<Row>[] = [
    { key: 'id', header: 'Customer ID', render: (r) => str(r, 'id') ?? '—' },
    { key: 'email', header: 'Email', render: (r) => str(r, 'email') ?? '—' },
    { key: 'name', header: 'Name', render: (r) => str(r, 'name') ?? '—' },
    {
      key: 'created',
      header: 'Created',
      render: (r) => formatDateTime(str(r, 'created') ? Number(str(r, 'created')) * 1000 : null),
    },
  ];

  const webhookColumns: Column<WebhookEvent>[] = [
    { key: 'received_at', header: 'Received', render: (r) => formatDateTime(r.received_at) },
    { key: 'event_id', header: 'Event ID', render: (r) => r.event_id ?? '—' },
    { key: 'status', header: 'Status', render: (r) => <StatusPill status={r.status} /> },
    {
      key: 'signature_status',
      header: 'Signature',
      render: (r) => <StatusPill status={r.signature_status} />,
    },
  ];

  const modeLabel = status?.mode === 'live' ? 'Live' : 'Test';

  return (
    <PageShell
      title="Stripe"
      subtitle="Card payments, payment links, and webhook events for the accounting control plane."
    >
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Connection"
          value={status?.connected ? 'Connected' : status?.configured ? 'Error' : 'Not configured'}
          icon={<CreditCard size={18} />}
          tone={status?.connected ? 'success' : status?.configured ? 'warning' : 'neutral'}
        />
        <StatCard
          label="Mode"
          value={modeLabel}
          icon={<Link2 size={18} />}
          tone={status?.mode === 'live' ? 'danger' : 'info'}
        />
        <StatCard
          label="Webhook secret"
          value={status?.webhook_configured ? 'Configured' : 'Missing'}
          icon={<RefreshCw size={18} />}
          tone={status?.webhook_configured ? 'success' : 'warning'}
        />
        <StatCard
          label="Publishable key"
          value={status?.publishable_key_configured ? 'Set' : 'Optional'}
          icon={<CreditCard size={18} />}
          tone={status?.publishable_key_configured ? 'success' : 'neutral'}
        />
      </div>

      {status?.last_error && (
        <div className="mb-4 rounded-lg border border-zt-danger/40 bg-zt-danger/10 px-4 py-3 text-sm text-zt-danger">
          {status.last_error}
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-zt-border bg-zt-surface p-4 text-sm">
        <span className="text-zt-muted">Public checkout:</span>
        <a
          href={PAY_CHECKOUT_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-zt-primary hover:text-zt-accent"
        >
          pay.zerotail.org <ExternalLink size={14} />
        </a>
        <span className="text-zt-muted">·</span>
        <span className="text-zt-muted">Webhook endpoint:</span>
        <code className="rounded bg-zt-bg px-2 py-0.5 text-xs text-slate-300">{WEBHOOK_URL}</code>
      </div>

      <Tabs
        tabs={[
          { id: 'payments', label: 'Payments' },
          { id: 'customers', label: 'Customers' },
          { id: 'webhooks', label: 'Webhook events' },
        ]}
        active={tab}
        onChange={(id) => setTab(id as TabId)}
      />

      <FilterBar
        actions={
          <>
            <button type="button" onClick={() => void load()} className={secondaryButtonClass}>
              <RefreshCw size={16} /> Refresh
            </button>
            <button
              type="button"
              onClick={() => setLinkOpen(true)}
              disabled={!status?.configured}
              className={primaryButtonClass}
            >
              <Plus size={16} /> Create payment link
            </button>
          </>
        }
      >
        <span className="text-sm text-zt-muted">
          {tab === 'payments'
            ? 'Recent payment intents from Stripe'
            : tab === 'customers'
              ? 'Stripe customers'
              : 'Stripe webhook deliveries received by accounting'}
        </span>
      </FilterBar>

      {tab === 'webhooks' ? (
        <DataTable
          columns={webhookColumns}
          rows={webhookRows}
          rowKey={(r) => String(r.id)}
          loading={loading}
          emptyTitle="No webhook events"
          emptyMessage="Register the webhook URL in Stripe Dashboard to see events here."
        />
      ) : (
        <DataTable
          columns={tab === 'customers' ? customerColumns : paymentColumns}
          rows={rows}
          rowKey={(r, i) => str(r, 'id') ?? String(i)}
          loading={loading}
          emptyTitle={tab === 'customers' ? 'No customers' : 'No payments'}
          emptyMessage={
            status?.configured
              ? 'No Stripe data yet for this account.'
              : 'Add STRIPE_SECRET_KEY to .env and redeploy.'
          }
        />
      )}

      <CreatePaymentLinkModal
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        onCreated={() => void load()}
      />
    </PageShell>
  );
}

function CreatePaymentLinkModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [description, setDescription] = useState('');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setAmount('');
    setCurrency('USD');
    setDescription('');
    setInvoiceRef('');
  };

  const handleSubmit = async () => {
    if (!amount.trim()) {
      toast.error('Amount is required');
      return;
    }
    setSubmitting(true);
    const result = await createStripePaymentLink({
      amount: amount.trim(),
      currency,
      description: description.trim() || undefined,
      invoice_ref: invoiceRef.trim() || undefined,
    });
    setSubmitting(false);
    if (!result.ok || !result.data) return;

    const data = result.data;
    if (data.status === 'not_configured') {
      toast.error('Stripe is not configured');
      return;
    }
    if (data.status !== 'ok') {
      toast.error(String(data.error ?? 'Could not create payment link'));
      return;
    }

    const url = data.url as string | undefined;
    toast.success('Payment link created');
    reset();
    onClose();
    onCreated();
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create Stripe payment link"
      footer={
        <>
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="button" onClick={() => void handleSubmit()} disabled={submitting} className={primaryButtonClass}>
            {submitting ? 'Creating…' : 'Create link'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">Amount</label>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="100.00"
              className={`w-full ${controlClass}`}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">Currency</label>
            <input
              type="text"
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              className={`w-full ${controlClass}`}
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-200">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Consulting invoice"
            className={`w-full ${controlClass}`}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-200">Invoice reference (optional)</label>
          <input
            type="text"
            value={invoiceRef}
            onChange={(e) => setInvoiceRef(e.target.value)}
            placeholder="INV-12345"
            className={`w-full ${controlClass}`}
          />
        </div>
      </div>
    </Modal>
  );
}

export default Stripe;
